import { motion } from 'framer-motion'
import { Users, Shield } from 'lucide-react'
export function ServerInfoPage() {
  return (
    <div className="min-h-screen pt-24 pb-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hero Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-20"
        >
          <h1 className="text-2xl sm:text-3xl md:text-5xl lg:text-6xl font-starjedi text-cyan-400 mb-6 tracking-wider leading-tight">
            SERVER INFORMATION
          </h1>
          <p className="text-base sm:text-lg md:text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed px-4">
            Everything you need to know about our server, from gameplay features 
            to how to get started.
          </p>
        </motion.div>




        {/* Rules and Guidelines */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="mb-20"
        >
          <div className="text-center mb-12">
            <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-starjedi text-cyan-400 mb-4 tracking-wider leading-tight">
              SERVER RULES & GUIDELINES
            </h2>
            <p className="text-sm sm:text-base md:text-lg text-gray-300 max-w-2xl mx-auto px-4">
              Join the most respected community in the galaxy. These aren't just rules—they're the foundation of our legendary server.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <motion.div 
              className="holographic-border bg-slate-900/60 border border-cyan-500/30 rounded-lg p-6 hover:border-cyan-400/50 transition-all duration-300 pulse-glow"
              whileHover={{ scale: 1.02 }}
              transition={{ duration: 0.2 }}
            >
              <div className="flex items-center mb-6">
                <div className="p-3 bg-cyan-400/20 rounded-full mr-4">
                  <Shield className="w-8 h-8 text-cyan-400" />
                </div>
                <div>
                  <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-white mb-2">GALACTIC LAW</h3>
                  <p className="text-cyan-400 text-sm font-mono">Enforced by our elite security forces</p>
                </div>
              </div>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-cyan-400 rounded-full mt-2 flex-shrink-0"></div>
                  <p className="text-gray-300"><span className="text-cyan-400 font-semibold">Respect the Code:</span> Treat all players and staff with dignity and honor</p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-cyan-400 rounded-full mt-2 flex-shrink-0"></div>
                  <p className="text-gray-300"><span className="text-cyan-400 font-semibold">Zero Tolerance:</span> No harassment, discrimination, or hate speech will be tolerated</p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-cyan-400 rounded-full mt-2 flex-shrink-0"></div>
                  <p className="text-gray-300"><span className="text-cyan-400 font-semibold">Fair Play:</span> No cheating, exploiting, or botting—earn your victories</p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-cyan-400 rounded-full mt-2 flex-shrink-0"></div>
                  <p className="text-gray-300"><span className="text-cyan-400 font-semibold">No RMT:</span> Real money trading and account selling are strictly forbidden</p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-cyan-400 rounded-full mt-2 flex-shrink-0"></div>
                  <p className="text-gray-300"><span className="text-cyan-400 font-semibold">Report Issues:</span> Help us maintain quality by reporting bugs and exploits</p>
                </div>
              </div>
            </motion.div>
            
            <motion.div 
              className="holographic-border bg-slate-900/60 border border-cyan-500/30 rounded-lg p-6 hover:border-cyan-400/50 transition-all duration-300 pulse-glow"
              whileHover={{ scale: 1.02 }}
              transition={{ duration: 0.2 }}
            >
              <div className="flex items-center mb-6">
                <div className="p-3 bg-cyan-400/20 rounded-full mr-4">
                  <Users className="w-8 h-8 text-cyan-400" />
                </div>
                <div>
                  <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-white mb-2">COMMUNITY SPIRIT</h3>
                  <p className="text-cyan-400 text-sm font-mono">The heart of our legendary server</p>
                </div>
              </div>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-cyan-400 rounded-full mt-2 flex-shrink-0"></div>
                  <p className="text-gray-300"><span className="text-cyan-400 font-semibold">Mentor New Pilots:</span> Guide newcomers and share your galactic wisdom</p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-cyan-400 rounded-full mt-2 flex-shrink-0"></div>
                  <p className="text-gray-300"><span className="text-cyan-400 font-semibold">Join the Events:</span> Participate in epic community events and competitions</p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-cyan-400 rounded-full mt-2 flex-shrink-0"></div>
                  <p className="text-gray-300"><span className="text-cyan-400 font-semibold">Positive Vibes:</span> Contribute constructively to all discussions</p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-cyan-400 rounded-full mt-2 flex-shrink-0"></div>
                  <p className="text-gray-300"><span className="text-cyan-400 font-semibold">Guild Honor:</span> Follow your guild and faction guidelines with pride</p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-cyan-400 rounded-full mt-2 flex-shrink-0"></div>
                  <p className="text-gray-300"><span className="text-cyan-400 font-semibold">Keep It Clean:</span> Maintain drama-free public channels for everyone's enjoyment</p>
                </div>
              </div>
            </motion.div>
          </div>
          
          <motion.div 
            className="mt-12 text-center"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            viewport={{ once: true }}
          >
            <div className="bg-gradient-to-r from-cyan-500/20 to-blue-500/20 border border-cyan-400/40 rounded-lg p-6 max-w-4xl mx-auto">
              <h3 className="text-lg sm:text-xl font-starjedi text-cyan-400 mb-4 tracking-wider">
                THE SWG: ORIGINS WAY
              </h3>
              <p className="text-gray-300 text-sm sm:text-base md:text-lg leading-relaxed px-4">
                These guidelines aren't just rules—they're the foundation of the most legendary Star Wars Galaxies server in the galaxy. 
                Join thousands of players who have chosen honor, respect, and epic adventures over chaos and toxicity.
              </p>
            </div>
          </motion.div>
        </motion.div>

      </div>
    </div>
  )
}
